<template>
  <!-- style="margin-left: 0; margin-right: 0; max-width: initial;" -->
  <q-layout v-if="!isLoading" view="lHh Lpr fFf">
    <!-- Header -->
    <q-header elevated class="bg-white text-dark">
    
      <div v-if="useCustomHeader" id="custom-header-content"></div>
      
      <!-- Default header with optional additional content -->
      <q-toolbar v-else>
        <!-- Left side -->
        <div class="row items-center no-wrap">
          <!-- Menu button (only mobile) -->
          <q-btn 
            v-if="$q.screen.lt.md"
            flat 
            round 
            icon="menu" 
            @click="leftDrawer = !leftDrawer"
            text-color="grey-7"
          />
          
          <!-- Teleport target for additional left header content -->
          <div id="additional-left-header-content"></div>
        </div>

        <!-- <q-space /> -->

        <!-- <img class="q-ma-none" src="/ai4soilhealth_4f.png" alt="App Logo" style="height:32px; max-width:120px;"> -->

        <q-space />

        <!-- Right side -->
        <div class="row items-center no-wrap">
          <!-- Teleport target for additional right header content -->
          <div id="additional-right-header-content"></div>
          
          <!-- Default header icons -->
          <q-btn flat round icon="notifications" @click="handleNotifications" text-color="grey-7" />
          <!-- <q-btn flat round @click="handleProfile">
            <q-avatar size="32px" color="grey-5" class="relative">
              <q-badge 
                floating 
                color="positive" 
                rounded 
                style="top: -2px; right: -2px;"
              />
            </q-avatar>
          </q-btn> -->
        </div>
      </q-toolbar>
    </q-header>

    <!-- Left Drawer (Sidebar) - automatically in desktop -->
    <q-drawer
      v-model="leftDrawer"
      :width="300"
      :breakpoint="768"
      bordered
      class="bg-grey-1"
      show-if-above
      
    >
    <SidebarComponent 
        :is-logged-in="isUserLoggedIn"
        :user="userData"
        @close="leftDrawer = false"
        @menu-click="handleMenuClick"
        @register="handleRegister"
      
        @signIn="handleSignIn"
        @user-profile="handleUserProfile"
      />
    </q-drawer>

    <!-- Main Content Area -->
    <q-page-container>
      <q-page class="column">
        <!-- Dynamic content based on active tab -->
        <div class="col">
          <component 
            :is="currentComponent" 
            v-bind="componentProps"
          />
        </div>
      </q-page>
    </q-page-container>

    <!-- Bottom Navigation (only mobile) -->
    <q-footer v-if="$q.screen.lt.md" class="bg-white">
      <q-tabs
        v-model="activeTab"
        class="text-grey-6"
        active-color="positive"
        indicator-color="transparent"
        @update:model-value="setActiveTab"
      >
        <q-tab 
          v-for="tab in tabs" 
          :key="tab.id"
          :name="tab.id" 
          :icon="tab.icon"
          :label="$t(tab.label)"
          class="q-tab--custom"
        />
      </q-tabs>
    </q-footer>
  </q-layout>
  <div v-else class="fixed-center">
    <q-spinner color="primary" size="3em" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useQuasar } from 'quasar'
import { Dark } from 'quasar'
import { useNavigation } from '@/common/composables/useNavigation.js'
import { useLanguage } from '@/common/composables/useLanguage'

// Navigation
const navigationConfig = {
  menuItems: [
    { id: 'home', label: 'Home', icon: 'home', public: true, tabs: true },
    { id: 'chat', label: 'Chat', icon: 'public', public: false, tabs: true },
    { id: 'catalog', label: 'Catalog', icon: 'menu_book', public: true },
    { id: 'social-feed', label: 'Feed', icon: 'groups', public: true, tabs: true },
  ],
  defaultTab: 'home',
  useCustomHeader: false
}

const {
  activeTab,
  activeView,
  tabs,
  setActiveTab,
  setActiveView,
  useCustomHeader,
  setAuthStatus
} = useNavigation(navigationConfig)

// Components
import HomeView from './views/home-view.vue'
import CatalogView from './views/catalog-view.vue'
import ChatView from './views/chat-view.vue'
import FeedView from './views/social-feed-view.vue'
import SidebarComponent from './layout/app-sidebar.vue'

import { useAuth } from '@/common/composables/useAuth.js'
import { useAppStore } from '@/common/composables/useAppStore.js'
import { useAppInit } from '@/common/composables/useAppInit.js'

const { keycloak, isAuthenticated, login, logout } = useAuth()
const { userData, isOnline, initStore} = useAppStore()
const { initializeUser } = useAppInit()
const { initializeLanguage, currentLocaleShort } = useLanguage()


// Loading state
const isLoading = ref(true)



// Custom header component
// const customHeader = ref(null)

// Initialize app
onMounted(async () => {
  await initializeLanguage()
  // Učitaj dark mode iz localStorage ODMAH
  const savedDark = localStorage.getItem("dark")
  if (savedDark !== null) {
    Dark.set(savedDark === "true")
  }
  
  // Učitaj font settings
  const dyslexia = localStorage.getItem("dyslexia") === "true"
  const zoomLevel = parseInt(localStorage.getItem("zoomlevel") ?? 1)
  
  // Primijeni dyslexic font
  if (dyslexia) {
    document.body.classList.add('dyslexic')
  }
  
  // Primijeni font size
  for (let i = 1; i <= 4; i++) {
    document.body.classList.remove(`font-scale-${i}`)
  }
  document.body.classList.add(`font-scale-${zoomLevel}`)
  
  // Onda inicijaliziraj usera
  await initializeUser()
  isLoading.value = false
})

// Props
const props = defineProps({
  user: {
    type: Object,
    default: () => ({ 
      first_name: 'Guest',
      email: 'guest@example.com',
      avatar: null 
    })
  },
  isLoggedIn: {
    type: Boolean,
    default: false
  }
})

// Quasar instance
const $q = useQuasar()




watch(isAuthenticated, (newValue) => {
  setAuthStatus(newValue)
}, { immediate: true })

// watch(() => $q.screen.gt.md, (isDesktop) => {
//   if (isDesktop) {
//     leftDrawer.value = true
//   }
// }, { immediate: true })

// Drawer state
const leftDrawer = ref(false)

// Map selection state
const selectedMapId = ref(1)

// Computed properties
const isUserLoggedIn = computed(() => isAuthenticated.value ?? isLoggedIn)

// Handle functions
const handleNotifications = () => {
  console.log('Notifications clicked')
}

const handleProfile = () => {
  console.log('Profile clicked')
}

const handleMapSelect = (mapId) => {
  selectedMapId.value = mapId
}

// Sidebar handlers
const handleMenuClick = (menuId) => {
  console.log('Menu clicked:', menuId)
  
  // Handle navigation based on menu item
  switch (menuId) {
    case 'home':
      setActiveView('home')
      break
    case 'chat':
      setActiveView('chat')
      break
    case 'catalog':
      setActiveView('catalog')
      break
    case 'my-project':
      // Navigate to my project
      console.log('Navigate to my project')
      break
    case 'social-feed':
      setActiveView('social-feed')
      break
    case 'support':
      // Navigate to support
      console.log('Navigate to support')
      break
  }
  
  // Close drawer on mobile after menu click
  if ($q.screen.lt.md) {
    leftDrawer.value = false
  }
}

const handleRegister = () => {
  console.log('Register clicked')
  keycloak.register({
    locale: currentLocaleShort.value //'en'
  });
  // Handle registration logic
}

const handleSignIn = () => {
  console.log('Sign up clicked')
  keycloak.login({
    locale: currentLocaleShort.value //'en'
  });
}


const handleUserProfile = () => {
  console.log('User profile clicked')
  // keycloak.logout(); //izbačeno nakon dodavanja settingsa za profil
  // Handle user profile navigation
  // Close drawer on mobile
  if ($q.screen.lt.md) {
    leftDrawer.value = false
  }
}


// Component selection based on active tab
const currentComponent = computed(() => {
  switch (activeView.value) {
    case 'chat':
      return ChatView
    case 'catalog':
      return CatalogView
    case 'social-feed':
      return FeedView
    case 'home':
    default:
      return HomeView
  }
})

const componentProps = computed(() => {
  const baseProps = {
    // ...currentProps,
    showDesktopLayout: $q.screen.gt.sm
  }
  
  switch (activeTab.value) {
    case 'home':
      return {
        ...baseProps,
        user: userData.value ?? props.user
      }
    default:
      return baseProps
  }
})

</script>

<style lang="scss" scoped>

.project-logo {
  width: 3rem;
  height: 3rem;
  object-fit: contain;
  
  &:hover {
    opacity: 0.8;
    transition: opacity var(--transition-normal);
  }
}

.q-header {
  body.body--dark & {
    background-color: $dark !important;
    color: white !important;
  }
}

.q-footer {
  body.body--dark & {
    background-color: $dark !important;
    
    .q-tabs {
      background-color: $dark;
    }
  }
}

.q-drawer {
  body.body--dark & {
    background-color: $dark !important;
  }
}

// Custom tab styling za bottom navigation
.q-tab--custom {
  .q-tab__content {
    flex-direction: column;
    
    .q-tab__icon {
      font-size: 2rem;
      margin-bottom: var(--radius-sm);
    }
    
    .q-tab__label {
      font-size: 0.625rem;
      line-height: 1;
    }
  }
}

// Mobile specific layout adjustments
.q-layout {
  @media (max-width: 48rem), (max-height: 48rem)  {
    .q-footer .q-tabs {
      border-radius: var(--radius-xl) var(--radius-xl) 0 0;
      box-shadow: var(--shadow-md);
    }
  }
}

@media (min-width: 112rem) {
  .q-page-container {
    padding-left: 0 !important;
  }

  .q-layout {
        padding: 0 5rem;
  }
}

// Desktop layout styling
@media (min-width: 48rem) {
  .q-layout {
      max-width: 75rem;
     margin: var(--gap-md) auto;
    border-radius: var(--radius-xl);
    overflow: hidden;
    box-shadow: var(--shadow-md);
    height: calc(100vh - 2.5rem);
 
  }


 
  body {
    background-color: var(--surface-light);
  }
}
</style>